import { useState, useCallback, useMemo } from 'react';
import { useForm, useController } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';
import { DateInput } from '@mantine/dates';
import { Loader } from '@mantine/core';
import '@mantine/dates/styles.css';
import { Breadcrumbs, OptionsTable } from '@components';
import {
  Button,
  ControlledInput,
  Select,
  MultiSelect,
  Modal,
  DoubleCheckModal,
  Switch,
  QuietAddTrigger,
  DotMenu,
  RefetchButton,
} from '@shared/ui';
import {
  usePromoCodes,
  useCreatePromoCode,
  useUpdatePromoCode,
  useDeletePromoCode,
  PROMO_DISCOUNT_TYPE_LABELS,
  promoCodeFormSchema,
  buildPromoCodePushMessage,
  type PromoCode,
  type CreatePromoCodeDto,
  type UpdatePromoCodeDto,
  type PromoDiscountType,
} from '@modules/promo-codes';
import { useSellerProductCategories, useProducts } from '@modules/products';
import { SELLER_ME_QUERY_KEY, sellerOnboardingApi } from '@modules/seller-onboarding/api';
import '@shared/styles/page.css';
import '@shared/styles/status-tag.css';
import './PromoCodesPage.css';

function formatDiscount(p: PromoCode): string {
  return p.discountType === 'percentage'
    ? `${p.discountValue}%`
    : `${p.discountValue} ₽`;
}

function formatMinCart(p: PromoCode): string {
  return p.minimumCartAmount != null ? `${p.minimumCartAmount} ₽` : '—';
}
const DISCOUNT_TYPE_OPTIONS = (
  Object.entries(PROMO_DISCOUNT_TYPE_LABELS) as [PromoDiscountType, string][]
).map(([value, label]) => ({ value, label }));

interface PromoCodeFormData {
  code: string;
  description: string;
  discountType: CreatePromoCodeDto['discountType'];
  discountValue: number | undefined;
  minimumCartAmount: string;
  isActive: boolean;
  usageLimitTotal: string;
  usageLimitPerUser: string;
  validFrom: Date | null;
  validUntil: Date | null;
  appliesToCategoryId: number | null;
  appliesToProductIds: number[];
  excludeProductIds: number[];
  sendPushNotification: boolean;
}

const defaultFormValues: PromoCodeFormData = {
  code: '',
  description: '',
  discountType: 'percentage',
  discountValue: undefined,
  minimumCartAmount: '',
  isActive: true,
  usageLimitTotal: '',
  usageLimitPerUser: '',
  validFrom: null,
  validUntil: null,
  appliesToCategoryId: null,
  appliesToProductIds: [],
  excludeProductIds: [],
  sendPushNotification: false,
};

const PROMO_DATE_TZ = 'Europe/Moscow';

/** Календарный день из DateInput → ISO без сдвига суток (полдень UTC). */
function toPromoDateIso(v: Date | string | null | undefined): string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'string') {
    const parsed = new Date(v);
    if (Number.isNaN(parsed.getTime())) return v;
    return toPromoDateIso(parsed);
  }
  if (!(v instanceof Date) || Number.isNaN(v.getTime())) return undefined;
  return new Date(
    Date.UTC(v.getFullYear(), v.getMonth(), v.getDate(), 12, 0, 0),
  ).toISOString();
}

/** ISO с бэка → локальная дата с тем же календарным днём, что в таблице (МСК). */
function fromPromoDateIso(iso: string): Date {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return d;
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: PROMO_DATE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  return new Date(Number(map.year), Number(map.month) - 1, Number(map.day));
}

function formatPromoDate(iso: string): string {
  const d = fromPromoDateIso(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

function formatPromoPeriod(p: PromoCode): string {
  const from = p.validFrom ? formatPromoDate(p.validFrom) : null;
  const until = p.validUntil ? formatPromoDate(p.validUntil) : null;
  if (!from && !until) return 'бессрочно';
  if (from && until) return `${from} – ${until}`;
  if (from) return `${from} –`;
  return `– ${until}`;
}

function promoToFormData(p: PromoCode): PromoCodeFormData {
  return {
    code: p.code,
    description: p.description ?? '',
    discountType: p.discountType,
    discountValue: p.discountValue,
    minimumCartAmount: p.minimumCartAmount != null ? String(p.minimumCartAmount) : '',
    isActive: p.isActive,
    usageLimitTotal: p.usageLimitTotal != null ? String(p.usageLimitTotal) : '',
    usageLimitPerUser: p.usageLimitPerUser != null ? String(p.usageLimitPerUser) : '',
    validFrom: p.validFrom ? fromPromoDateIso(p.validFrom) : null,
    validUntil: p.validUntil ? fromPromoDateIso(p.validUntil) : null,
    appliesToCategoryId: p.appliesToCategoryId ?? null,
    appliesToProductIds: p.appliesToProductIds ?? [],
    excludeProductIds: p.excludeProductIds ?? [],
    sendPushNotification: false,
  };
}

export default function PromoCodesPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingPromoId, setEditingPromoId] = useState<number | null>(null);
  const [promoToDelete, setPromoToDelete] = useState<PromoCode | null>(null);
  const {
    data: promoCodes = [],
    isLoading: listLoading,
    error: listError,
    refetch,
    isFetching: listFetching,
  } = usePromoCodes();
  const { data: sellerCategories = [] } = useSellerProductCategories(isCreateModalOpen);
  const { data: products = [] } = useProducts();
  const { data: sellerApp } = useQuery({
    queryKey: SELLER_ME_QUERY_KEY,
    queryFn: () => sellerOnboardingApi.getMe(),
  });
  const shopName =
    sellerApp?.seller?.shopName?.trim() || sellerApp?.shopName?.trim() || '';
  const createPromo = useCreatePromoCode();
  const updatePromo = useUpdatePromoCode();
  const deletePromo = useDeletePromoCode();

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm<PromoCodeFormData>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(promoCodeFormSchema) as any,
    mode: 'onSubmit',
  });

  const { field: isActiveField } = useController({ name: 'isActive', control });
  const { field: discountTypeField } = useController({ name: 'discountType', control });
  const { field: validFromField } = useController({ name: 'validFrom', control });
  const { field: validUntilField } = useController({ name: 'validUntil', control });
  const { field: appliesToCategoryIdField } = useController({
    name: 'appliesToCategoryId',
    control,
  });
  const { field: appliesToProductIdsField } = useController({
    name: 'appliesToProductIds',
    control,
  });
  const { field: excludeProductIdsField } = useController({
    name: 'excludeProductIds',
    control,
  });
  const { field: sendPushNotificationField } = useController({
    name: 'sendPushNotification',
    control,
  });

  const watchedCode = watch('code');
  const watchedDiscountType = watch('discountType');
  const watchedDiscountValue = watch('discountValue');
  const watchedValidFrom = watch('validFrom');
  const watchedValidUntil = watch('validUntil');
  const watchedCategoryId = watch('appliesToCategoryId');
  const watchedProductIds = watch('appliesToProductIds');

  const categoryOptions = useMemo(
    () => sellerCategories.map((c) => ({ value: c.id.toString(), label: c.name })),
    [sellerCategories],
  );

  const productOptions = useMemo(
    () =>
      products.map((p) => {
        const categoryNames =
          p.categories?.map((c) => c.category.name).filter(Boolean).join(', ') || '';
        const label = categoryNames
          ? `ID: ${p.id} ${p.name} (${categoryNames})`
          : `ID: ${p.id} ${p.name}`;
        return { value: p.id.toString(), label };
      }),
    [products],
  );

  const pushPreview = useMemo(() => {
    const categoryName =
      watchedCategoryId != null
        ? sellerCategories.find((c) => c.id === watchedCategoryId)?.name ?? null
        : null;
    const productNames = (watchedProductIds ?? [])
      .map((id) => products.find((p) => p.id === id)?.name)
      .filter((n): n is string => Boolean(n));
    return buildPromoCodePushMessage({
      code: watchedCode || '',
      discountType: watchedDiscountType,
      discountValue: Number(watchedDiscountValue) || 0,
      shopName,
      validFrom: watchedValidFrom,
      validUntil: watchedValidUntil,
      categoryName,
      productNames,
    });
  }, [
    watchedCode,
    watchedDiscountType,
    watchedDiscountValue,
    shopName,
    watchedValidFrom,
    watchedValidUntil,
    watchedCategoryId,
    watchedProductIds,
    sellerCategories,
    products,
  ]);

  const isFormPending = createPromo.isPending || updatePromo.isPending;

  const onSubmit = async (data: PromoCodeFormData) => {
    const isEdit = editingPromoId != null;
    const payload: CreatePromoCodeDto = {
      code: data.code.trim(),
      description: data.description.trim() || undefined,
      discountType: data.discountType,
      discountValue: Number(data.discountValue),
      minimumCartAmount: data.minimumCartAmount?.trim()
        ? Number(data.minimumCartAmount)
        : undefined,
      isActive: data.isActive,
      usageLimitTotal: data.usageLimitTotal?.trim()
        ? Number(data.usageLimitTotal)
        : undefined,
      usageLimitPerUser: data.usageLimitPerUser?.trim()
        ? Number(data.usageLimitPerUser)
        : undefined,
      validFrom: toPromoDateIso(data.validFrom),
      validUntil: toPromoDateIso(data.validUntil),
      appliesToCategoryId: data.appliesToCategoryId ?? undefined,
      appliesToProductIds: data.appliesToProductIds?.length
        ? data.appliesToProductIds
        : undefined,
      excludeProductIds: data.excludeProductIds?.length
        ? data.excludeProductIds
        : undefined,
    };
    if (isEdit) {
      const updateData: UpdatePromoCodeDto = {
        ...payload,
        description: data.description.trim() || null,
        minimumCartAmount: payload.minimumCartAmount ?? null,
        usageLimitTotal: payload.usageLimitTotal ?? null,
        usageLimitPerUser: payload.usageLimitPerUser ?? null,
        appliesToCategoryId: data.appliesToCategoryId ?? null,
        appliesToProductIds: data.appliesToProductIds ?? [],
        excludeProductIds: data.excludeProductIds ?? [],
        validFrom: data.validFrom == null ? null : toPromoDateIso(data.validFrom),
        validUntil: data.validUntil == null ? null : toPromoDateIso(data.validUntil),
      };
      await updatePromo.mutateAsync({ id: editingPromoId, data: updateData });
    } else {
      await createPromo.mutateAsync({
        ...payload,
        sendPushNotification: data.sendPushNotification || undefined,
      });
    }
    reset(defaultFormValues);
    setEditingPromoId(null);
    setIsCreateModalOpen(false);
  };

  const openCreateModal = () => {
    setEditingPromoId(null);
    reset(defaultFormValues);
    setIsCreateModalOpen(true);
  };

  const openEditModal = useCallback(
    (p: PromoCode) => {
      setEditingPromoId(p.id);
      reset(promoToFormData(p));
      setIsCreateModalOpen(true);
    },
    [reset],
  );

  const closeCreateModal = () => {
    if (!isFormPending) {
      setIsCreateModalOpen(false);
      setEditingPromoId(null);
      reset(defaultFormValues);
    }
  };

  const confirmDelete = useCallback(() => {
    if (!promoToDelete) return;
    deletePromo.mutateAsync(promoToDelete.id).then(() => {
      setPromoToDelete(null);
    });
  }, [promoToDelete, deletePromo]);

  const columns: {
    key: string;
    label: string;
    render?: (item: PromoCode) => React.ReactNode;
  }[] = [
      {
        key: 'code',
        label: 'Код',
        render: (p) => <span className="promocodes-code">{p.code}</span>,
      },
      { key: 'description', label: 'Описание', render: (p) => p.description || '—' },
      {
        key: 'discountValue',
        label: 'Скидка',
        render: (p) => formatDiscount(p),
      },
      {
        key: 'minimumCartAmount',
        label: 'Мин. сумма',
        render: (p) => formatMinCart(p),
      },
      {
        key: 'isActive',
        label: 'Статус',
        render: (p) => (
          <span
            className="status-tag"
            style={
              p.isActive
                ? { backgroundColor: '#dcfce7', color: '#166534' }
                : { backgroundColor: '#f3f4f6', color: '#6b7280' }
            }
          >
            {p.isActive ? 'Активен' : 'Неактивен'}
          </span>
        ),
      },
      {
        key: 'usageLimitTotal',
        label: 'Лимит всего',
        render: (p) => p.usageLimitTotal ?? '∞',
      },
      {
        key: 'usageLimitPerUser',
        label: 'На пользователя',
        render: (p) => p.usageLimitPerUser ?? '∞',
      },
      {
        key: 'usagesCount',
        label: 'Использований',
        render: (p) => p.usagesCount ?? 0,
      },
      {
        key: 'validFrom',
        label: 'С',
        render: (p) => (p.validFrom ? formatPromoDate(p.validFrom) : '—'),
      },
      {
        key: 'validUntil',
        label: 'До',
        render: (p) => (p.validUntil ? formatPromoDate(p.validUntil) : '—'),
      },
    ];

  const getActions = useCallback(
    (item: PromoCode) => [
      { label: 'Редактировать', onClick: () => openEditModal(item) },
      { label: 'Удалить', onClick: () => setPromoToDelete(item) },
    ],
    [openEditModal],
  );

  return (
    <div className="page-container promocodes-page">
      <Breadcrumbs items={[{ label: 'Главная', path: '/' }, { label: 'Промокоды' }]} />

      <div className="page-header">
        <div className="page-header-top">
          <div className="promocodes-page-title-row">
            <h1>Промокоды</h1>
            <QuietAddTrigger onClick={openCreateModal} aria-label="Создать промокод">
              Добавить промокод
            </QuietAddTrigger>
          </div>
        </div>
        <p className="promocodes-page-subtitle">
          Создавайте скидочные коды для покупателей вашего магазина — процент или фиксированная
          сумма, с лимитами и сроком действия.
        </p>
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title={
          <div className="promocodes-modal-title">
            <span>
              {editingPromoId != null ? 'Редактировать промокод' : 'Создать промокод'}
            </span>
            <Switch
              className="promocodes-modal-active-switch"
              label="Активен"
              checked={isActiveField.value}
              onChange={(e) => isActiveField.onChange(e.currentTarget.checked)}
              onBlur={isActiveField.onBlur}
              disabled={isFormPending}
            />
          </div>
        }
        size={900}
      >
        <form onSubmit={handleSubmit(onSubmit)} className="promocodes-form">
          <div className="promocodes-form-grid">
            <div className="promocodes-form-field">
              <ControlledInput
                id="code"
                type="text"
                label="Код *"
                name="code"
                control={control}
                error={errors.code}
                placeholder="SPRING20"
                description="Уникальный код для покупателя"
                disabled={isFormPending}
              />
            </div>
            <div className="promocodes-form-field">
              <ControlledInput
                id="description"
                type="text"
                label="Описание"
                name="description"
                control={control}
                error={errors.description}
                placeholder="Скидка 20% на весенние букеты"
                description="Видно только вам в списке промокодов"
                disabled={isFormPending}
              />
            </div>
            <div className="promocodes-form-field">
              <Select
                label="Тип скидки *"
                placeholder="Выберите тип"
                data={DISCOUNT_TYPE_OPTIONS}
                value={discountTypeField.value}
                onChange={(value) =>
                  discountTypeField.onChange((value as PromoDiscountType) ?? 'percentage')
                }
                description="Процент от суммы заказа или фиксированная сумма в рублях"
                error={errors.discountType?.message}
                disabled={isFormPending}
              />
            </div>
            <div className="promocodes-form-field">
              <ControlledInput
                id="discountValue"
                type="number"
                label="Значение скидки *"
                name="discountValue"
                control={control}
                error={errors.discountValue}
                placeholder="20"
                min={0}
                step="0.01"
                description="Для процента — от 1 до 100; для суммы — размер скидки в ₽"
                disabled={isFormPending}
              />
            </div>
            <div className="promocodes-form-field">
              <ControlledInput
                id="usageLimitTotal"
                type="text"
                inputMode="numeric"
                label="Лимит использований всего"
                name="usageLimitTotal"
                control={control}
                error={errors.usageLimitTotal}
                placeholder="100"
                description="Сколько раз промокод можно применить всеми покупателями. Пусто — безлимит"
                disabled={isFormPending}
              />
            </div>
            <div className="promocodes-form-field">
              <ControlledInput
                id="usageLimitPerUser"
                type="text"
                inputMode="numeric"
                label="Лимит на пользователя"
                name="usageLimitPerUser"
                control={control}
                error={errors.usageLimitPerUser}
                placeholder="1"
                description="Сколько раз один покупатель может использовать код. 1 — одноразовый"
                disabled={isFormPending}
              />
            </div>
            <div className="promocodes-form-field">
              <DateInput
                className="promocodes-date-input custom-date-input"
                label="Действует с"
                placeholder="Не указано"
                description="Пусто — действует сразу после создания"
                valueFormat="DD.MM.YYYY"
                value={validFromField.value}
                onChange={(v) => validFromField.onChange(v)}
                onBlur={validFromField.onBlur}
                error={errors.validFrom?.message}
                clearable
                disabled={isFormPending}
              />
            </div>
            <div className="promocodes-form-field">
              <DateInput
                className="promocodes-date-input custom-date-input"
                label="Действует до"
                placeholder="Не указано"
                description="Пусто — без срока окончания"
                valueFormat="DD.MM.YYYY"
                value={validUntilField.value}
                onChange={(v) => validUntilField.onChange(v)}
                onBlur={validUntilField.onBlur}
                error={errors.validUntil?.message}
                clearable
                disabled={isFormPending}
              />
            </div>
            <div className="promocodes-form-field promocodes-form-field--full">
              <ControlledInput
                id="minimumCartAmount"
                type="text"
                inputMode="decimal"
                label="Минимальная сумма корзины"
                name="minimumCartAmount"
                control={control}
                error={errors.minimumCartAmount}
                placeholder="0"
                description="Пусто — без ограничения по сумме корзины"
                disabled={isFormPending}
              />
            </div>
            <div className="promocodes-form-field promocodes-form-field--full">
              <Select
                label="Только для категории"
                placeholder="Все категории"
                data={categoryOptions}
                value={appliesToCategoryIdField.value}
                onChange={(value) => appliesToCategoryIdField.onChange(value)}
                searchable
                clearable
                description="Если выбрана — скидка только на товары этой категории"
                disabled={isFormPending}
              />
            </div>
            <div className="promocodes-form-field promocodes-form-field--full">
              <MultiSelect
                label="Только для товаров"
                placeholder="Все товары"
                data={productOptions}
                value={appliesToProductIdsField.value}
                onChange={(values) => appliesToProductIdsField.onChange(values)}
                searchable
                description="Если выбраны — скидка только на эти товары (приоритет над категорией)"
                disabled={isFormPending}
              />
            </div>
            <div className="promocodes-form-field promocodes-form-field--full">
              <MultiSelect
                label="Исключить товары"
                placeholder="Ничего не исключено"
                data={productOptions}
                value={excludeProductIdsField.value}
                onChange={(values) => excludeProductIdsField.onChange(values)}
                searchable
                description="Выбранные товары не участвуют в скидке по промокоду"
                disabled={isFormPending}
              />
            </div>
            {editingPromoId == null && (
              <div className="promocodes-form-field promocodes-form-field--full">
                <div className="promocodes-push-block">
                  <Switch
                    className="promocodes-push-switch"
                    label="Разослать пуш всем клиентам приложения"
                    checked={sendPushNotificationField.value}
                    onChange={(e) =>
                      sendPushNotificationField.onChange(e.currentTarget.checked)
                    }
                    onBlur={sendPushNotificationField.onBlur}
                    disabled={isFormPending}
                  />
                  <div className="promocodes-push-preview" aria-live="polite">
                    <div className="promocodes-push-preview-title">{pushPreview.title}</div>
                    <div className="promocodes-push-preview-body">{pushPreview.body}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="promocodes-form-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={closeCreateModal}
              disabled={isFormPending}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isFormPending}
              loading={isFormPending}
            >
              {editingPromoId != null ? 'Сохранить' : 'Создать'}
            </Button>
          </div>
        </form>
      </Modal>

      <DoubleCheckModal
        isOpen={promoToDelete != null}
        onClose={() => setPromoToDelete(null)}
        title="Удалить промокод?"
        message={
          promoToDelete ? `Промокод «${promoToDelete.code}» будет удалён.` : undefined
        }
        firstButtonText="Отмена"
        secondButtonText="Удалить"
        secondButtonAction={confirmDelete}
        isLoading={deletePromo.isPending}
      />

      <div className="content-wrapper promocodes-content">
        <div className="promocodes-table-scroll">
          <div className="promocodes-desktop-table">
            <OptionsTable<PromoCode>
              columns={columns}
              data={promoCodes}
              loading={listLoading || listFetching}
              error={listError}
              refetch={refetch}
              getActions={getActions}
              emptyMessage="Промокодов пока нет"
            />
          </div>

          <div className="promocodes-mobile-list" aria-label="Список промокодов">
            {listError && refetch ? (
              <RefetchButton
                refetch={refetch}
                isLoading={listLoading || listFetching}
                errorMessage={
                  listError instanceof Error
                    ? listError.message
                    : 'Не удалось загрузить данные'
                }
              />
            ) : listLoading || listFetching ? (
              <div className="empty-container">
                <Loader />
              </div>
            ) : promoCodes.length === 0 ? (
              <div className="promocodes-page__empty" role="status">
                Промокодов пока нет
              </div>
            ) : (
              promoCodes.map((promo) => (
                <article key={promo.id} className="promocodes-mobile-card">
                  <div className="promocodes-mobile-card__top">
                    <div className="promocodes-mobile-card__heading">
                      <span className="promocodes-code">{promo.code}</span>
                      <span
                        className="status-tag"
                        style={
                          promo.isActive
                            ? { backgroundColor: '#dcfce7', color: '#166534' }
                            : { backgroundColor: '#f3f4f6', color: '#6b7280' }
                        }
                      >
                        {promo.isActive ? 'Активен' : 'Неактивен'}
                      </span>
                    </div>
                    <DotMenu items={getActions(promo)} />
                  </div>
                  {promo.description ? (
                    <p className="promocodes-mobile-card__desc">{promo.description}</p>
                  ) : null}
                  <div className="promocodes-mobile-card__metrics">
                    <div className="promocodes-mobile-card__metric">
                      <span className="promocodes-mobile-card__label">Скидка</span>
                      <span className="promocodes-mobile-card__value">
                        {formatDiscount(promo)}
                      </span>
                    </div>
                    <div className="promocodes-mobile-card__metric">
                      <span className="promocodes-mobile-card__label">Мин. сумма</span>
                      <span className="promocodes-mobile-card__value">
                        {formatMinCart(promo)}
                      </span>
                    </div>
                    <div className="promocodes-mobile-card__metric">
                      <span className="promocodes-mobile-card__label">Исп.</span>
                      <span className="promocodes-mobile-card__value">
                        {promo.usagesCount ?? 0}
                        {promo.usageLimitTotal != null
                          ? ` / ${promo.usageLimitTotal}`
                          : ''}
                      </span>
                    </div>
                  </div>
                  <div className="promocodes-mobile-card__row">
                    <span className="promocodes-mobile-card__label">Срок</span>
                    <span className="promocodes-mobile-card__value">
                      {formatPromoPeriod(promo)}
                    </span>
                  </div>
                </article>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
